import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { DurationRangeSlider } from '~/components/duration-range-slider'
import { useCurrentOrganization } from '~/hooks'
import { OrganizationMutators } from '~/mutators/organizations'
import { SettingsContent, useSettingTitle } from '~/templates/settings'

import {
  Form,
  FormErrorAlert,
  FormField,
  FormFooterControl,
  Heading2,
  Heading3,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Switch,
  processFormError,
} from '@hap/components'

const formSchema = z
  .object({
    recordingDurationMin: z.number().int().min(0),
    recordingDurationMax: z.number().int().min(0).nullable(),
    messageCountMin: z.number().int().min(0).nullable(),
    primaryAgentInvolvement: z.number().int().min(1).max(4),
    speakerRatioEnabled: z.boolean(),
    speakerRatioLimit: z.number().int().min(50).max(100),
    excludeVoicemails: z.boolean(),
    excludePossibleAgentAssignmentIssues: z.boolean(),
  })
  .refine(
    ({ recordingDurationMin, recordingDurationMax }) =>
      recordingDurationMax === null || recordingDurationMin <= recordingDurationMax,
    {
      path: ['recordingDurationMax'],
      message: 'Maximum duration must be greater than or equal to minimum duration',
    },
  )
type FormValues = z.infer<typeof formSchema>

const INVOLVEMENT_OPTIONS = [
  { value: 1, label: 'At least 80%' },
  { value: 2, label: 'At least 60% (Recommended)' },
  { value: 3, label: 'At least 40%' },
  { value: 4, label: 'Any involvement' },
]

export const Route = createFileRoute(
  '/_authenticated/$alias/settings/auto-qa-preferences/',
)({
  component: AutoQaSettings,
})

function AutoQaSettings() {
  const organization = useCurrentOrganization()
  const { update } = OrganizationMutators.useUpdate()

  useSettingTitle(['Auto QA Preferences'])

  const defaultValues = useMemo((): FormValues => {
    const scorecard = organization.orgSettings?.scorecard
    return {
      recordingDurationMin: scorecard?.recordingDuration?.min ?? 0,
      recordingDurationMax: scorecard?.recordingDuration?.max ?? null,
      messageCountMin: scorecard?.messageCountMin ?? null,
      primaryAgentInvolvement: scorecard?.primaryAgentInvolvement ?? 2,
      speakerRatioEnabled: (scorecard?.speakerRatioLimit ?? 0) > 0,
      speakerRatioLimit: scorecard?.speakerRatioLimit ?? 75,
      excludeVoicemails: scorecard?.excludeVoicemails ?? false,
      excludePossibleAgentAssignmentIssues:
        scorecard?.excludePossibleAgentAssignmentIssues ?? true,
    }
  }, [organization])

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: defaultValues,
  })

  const speakerRatioEnabled = form.watch('speakerRatioEnabled')
  const speakerRatioLimit = form.watch('speakerRatioLimit')
  const recordingDurationMin = form.watch('recordingDurationMin')
  const recordingDurationMax = form.watch('recordingDurationMax')

  const handleMinDurationChange = useCallback(
    (seconds: number) => {
      form.setValue('recordingDurationMin', seconds, { shouldDirty: true })
    },
    [form],
  )

  const handleMaxDurationChange = useCallback(
    (seconds: number | null) => {
      form.setValue('recordingDurationMax', seconds, { shouldDirty: true })
    },
    [form],
  )

  const handleSubmit = form.handleSubmit(async (values: FormValues) => {
    try {
      await update({
        settings: {
          scorecard: {
            recordingDuration: {
              min: values.recordingDurationMin,
              max: values.recordingDurationMax,
            },
            messageCountMin: values.messageCountMin,
            primaryAgentInvolvement: values.primaryAgentInvolvement,
            speakerRatioLimit: values.speakerRatioEnabled
              ? values.speakerRatioLimit
              : null,
            excludeVoicemails: values.excludeVoicemails,
            excludePossibleAgentAssignmentIssues:
              values.excludePossibleAgentAssignmentIssues,
          },
        },
      })
    } catch (e) {
      processFormError(form.setError, e, {
        genericMessage: 'Failed to update Auto QA settings',
      })
    }
  })

  const handleReset = () => {
    form.reset(defaultValues)
  }

  return (
    <SettingsContent className="w-full">
      <Form {...form}>
        <FormErrorAlert />

        <form onSubmit={handleSubmit} className="space-y-12">
          <section className="space-y-4">
            <hgroup>
              <Heading2>Recording Duration Filter</Heading2>
              <p className="text-accent-foreground">
                Exclude interactions outside this duration range from automatic scoring.
              </p>
            </hgroup>

            <div className="rounded-lg border p-4">
              <DurationRangeSlider
                minValue={recordingDurationMin}
                maxValue={recordingDurationMax}
                onMinChange={handleMinDurationChange}
                onMaxChange={handleMaxDurationChange}
              />
            </div>
          </section>

          <section className="space-y-4">
            <hgroup>
              <Heading2>Message Count Filter</Heading2>
              <p className="text-accent-foreground">
                Exclude non-phone interactions with fewer messages than specified from
                automatic scoring.
              </p>
            </hgroup>

            <div className="bg-accent rounded-lg p-4">
              <div className="flex items-center gap-10">
                <div className="shrink grow">
                  <Heading3>Minimum messages required</Heading3>
                  <p className="text-accent-foreground">
                    Non-phone interactions with fewer messages will be excluded from
                    automatic scoring. Phone calls are not affected. Leave empty to
                    disable this filter.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="messageCountMin"
                  render={({ field }) => (
                    <Input
                      type="number"
                      min={0}
                      placeholder="No minimum"
                      className="w-32 shrink-0"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value
                        field.onChange(val === '' ? null : parseInt(val, 10))
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <hgroup>
              <Heading2>Multi-Agent Involvement</Heading2>
              <p className="text-accent-foreground">
                Filter by how much of the conversation the primary agent handled. Useful
                for excluding transferred calls.
              </p>
            </hgroup>

            <div className="bg-accent rounded-lg p-4">
              <div className="flex items-center gap-10">
                <div className="shrink grow">
                  <Heading3>Primary agent must handle</Heading3>
                  <p className="text-accent-foreground">
                    Interactions where the primary agent handled less than this threshold
                    will be excluded from automatic scoring.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="primaryAgentInvolvement"
                  render={({ field }) => (
                    <Select
                      value={String(field.value)}
                      onValueChange={(v) => field.onChange(parseInt(v, 10))}
                    >
                      <SelectTrigger className="w-80 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INVOLVEMENT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={String(opt.value)}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <hgroup>
              <Heading2>Speaker Ratio Limit</Heading2>
              <p className="text-accent-foreground">
                Exclude recordings where one speaker dominated the conversation (e.g.,
                long holds or monologues).
              </p>
            </hgroup>

            <div className="bg-accent space-y-4 rounded-lg p-4">
              <FormField
                control={form.control}
                name="speakerRatioEnabled"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                    <Label>Enable speaker ratio filter</Label>
                  </div>
                )}
              />

              {speakerRatioEnabled && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <Label>Exclude if either speaker talked more than</Label>
                    <span className="text-foreground font-medium">
                      {speakerRatioLimit}%
                    </span>
                  </div>
                  <FormField
                    control={form.control}
                    name="speakerRatioLimit"
                    render={({ field }) => (
                      <Slider
                        value={[field.value]}
                        onValueChange={([v]) => field.onChange(v)}
                        min={50}
                        max={100}
                        step={5}
                        disabled={!speakerRatioEnabled}
                      />
                    )}
                  />
                  <div className="text-muted-foreground flex justify-between text-xs">
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="space-y-4">
            <hgroup>
              <Heading2>Voicemail Filter</Heading2>
              <p className="text-accent-foreground">
                Exclude voicemail interactions from automatic scoring.
              </p>
            </hgroup>

            <div className="bg-accent rounded-lg p-4">
              <FormField
                control={form.control}
                name="excludeVoicemails"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                    <Label>Exclude voicemails from auto-evaluation</Label>
                  </div>
                )}
              />
            </div>
          </section>

          <section className="space-y-4">
            <hgroup>
              <Heading2>Agent Assignment Filter</Heading2>
              <p className="text-accent-foreground">
                Exclude interactions where the assigned agent may not match the agent who
                handled the conversation.
              </p>
            </hgroup>

            <div className="bg-accent rounded-lg p-4">
              <FormField
                control={form.control}
                name="excludePossibleAgentAssignmentIssues"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                    <Label>
                      Exclude possible agent assignment issues from auto-evaluation
                    </Label>
                  </div>
                )}
              />
            </div>
          </section>

          <FormFooterControl onReset={handleReset} submitLabel="Save changes" />
        </form>
      </Form>
    </SettingsContent>
  )
}
